import './PaidMessages.sass';

import React, { useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { Link, useRoute } from 'react-router5';

import { paidActions, paidSelector } from '$store/paid';
import { RootState } from '$store/rootReducer';
import { Header, Placeholder, ScrollView } from '$uikit';
import { Loader } from '$shared/components';
import { useCurrentLang, usePreparedHistory, useTranslator } from '$hooks';
import router from '$navigation/router';
import { OTHER, PROFILE } from '$shared/constants/pages';
import { Message } from '$core/Chat/Message/Message';
import { formatDate, scrollToBottom, transliterate } from '$utils';
import { PreparedHistoryItem } from '$hooks/usePreparedHistory';
import { PaidMessageModel } from '$store/models';
import { imActions } from '$store/im';

const isNeedGroup = (message: PreparedHistoryItem, otherMessage: PreparedHistoryItem) =>
  message.type === otherMessage.type && message.type === 'message';

export const PaidMessages = () => {
  const dispatch = useDispatch();
  const t = useTranslator();
  const { isMessagesLoading, messages, isMoreMessagesLoading, nextFrom } = useSelector(
    (state: RootState) => paidSelector(state),
  );
  const paidHistory = usePreparedHistory(messages);
  const currentLang = useCurrentLang();
  const { route } = useRoute();
  const isSoldMode = route.params.peer_id > 0;

  useEffect(() => {
    scrollToBottom('purchased_history');
    dispatch(imActions.loadTonRate());
  }, []);

  useEffect(() => {
    dispatch(paidActions.loadPaidMessages());
  }, [dispatch]);

  function renderContent() {
    if (isMessagesLoading && !paidHistory.length) {
      return <Loader isWrapped />;
    }

    if (!isMessagesLoading && !paidHistory.length) {
      return (
        <Placeholder centered>
          <div>{t('purchased_empty')}</div>
          <div>{t('purchased_placeholder')}</div>
        </Placeholder>
      );
    }

    return (
      <div className="Paid__history">
        {paidHistory.map((paidHistoryItem, index) => {
          if (paidHistoryItem.type === 'date') {
            return (
              <div className="Paid__history__date" key={paidHistoryItem.key}>
                {formatDate(new Date(paidHistoryItem.object as number), t)}
              </div>
            );
          } else {
            const message = paidHistoryItem.object as PaidMessageModel;
            const { fromId, userPhoto, userName } = message;
            const isGroupingBefore =
              index > 0 && isNeedGroup(paidHistoryItem, paidHistory[index - 1]);
            const isGroupingAfter =
              index < paidHistory.length - 1 &&
              isNeedGroup(paidHistoryItem, paidHistory[index + 1]);

            return (
              <div className="Paid__message__item" key={paidHistoryItem.key}>
                <Link routeName={PROFILE} routeParams={{ id: fromId }}>
                  <div className="Paid__message__avatar">
                    <div
                      className="Paid__message__avatar_item"
                      style={{ backgroundImage: `url(${userPhoto})` }}
                    />
                  </div>
                </Link>
                <div className="Paid__message__content">
                  <Link routeName={PROFILE} routeParams={{ id: fromId }}>
                    <span className="Paid__message__userName">
                      {currentLang.key !== 'ru' ? transliterate(userName) : userName}
                    </span>
                  </Link>
                  <Message
                    message={message}
                    peerId={fromId}
                    isGroupingBefore={isGroupingBefore}
                    isGroupingAfter={isGroupingAfter}
                    isOutOfChat
                  />
                </div>
              </div>
            );
          }
        })}
      </div>
    );
  }

  return (
    <>
      <Header showBackButton>
        {t(isSoldMode ? 'purchased_sold_content' : 'purchased_content')}
      </Header>
      <ScrollView
        className="Paid"
        id="purchased_history"
        canLoadMore={!isMessagesLoading && !isMoreMessagesLoading && !!nextFrom}
        onLoadMore={() => dispatch(paidActions.loadMorePaidMessages(nextFrom))}
        isLoadingMore={isMoreMessagesLoading}
        isReversedDirection
      >
        {renderContent()}
      </ScrollView>
    </>
  );
};
